<template>
    <div id="form-builder-page">
        <loader v-if="is('loading')"></loader>

        <template v-if="!is('loading')">
            <QuestionsConditions
                v-if="selectedQuestion"
                ref="questionsConditions"
                :value="selectedQuestion"
                :questions="questions"
                @update:conditions="updateConditions"
            />

            <header class="builder-header">
                <div class="builder-heading">
                    <kv-button
                        variant="text"
                        size="small"
                        prepend-icon="ui-arrow-left"
                        to="/forms"
                        :aria-label="t('forms-questions-btn_back')"
                    />
                    <div class="builder-title">
                        <span class="builder-eyebrow">{{ t('forms-questions-title') }}</span>
                        <!-- i18n-validator-ignore-next-line: runtime form name -->
                        <h1>{{ form ? form.name : '' }}</h1>
                    </div>
                </div>
                <div class="builder-header-actions">
                    <kv-button
                        variant="outlined"
                        size="small"
                        prepend-icon="ui-message-text-square-01"
                        :to="answersRoute"
                    >
                        {{ t('forms-questions-view_answers') }}
                    </kv-button>
                    <kv-button
                        variant="outlined"
                        size="small"
                        prepend-icon="ui-link-01"
                        @click="openPublicForm"
                    >
                        {{ t('forms-questions-open_public') }}
                    </kv-button>
                    <kv-button
                        variant="outlined"
                        size="small"
                        prepend-icon="ui-settings-02"
                        :aria-label="t('forms-appearance-tooltip')"
                        :title="t('forms-appearance-tooltip')"
                        @click="openAppearanceSettings"
                    />
                    <kv-button
                        variant="filled"
                        size="small"
                        prepend-icon="ui-plus"
                        @click="openNewQuestion"
                    >
                        {{ t('forms-questions-btn_add_question') }}
                    </kv-button>
                </div>
            </header>

            <div class="builder-grid">
                <div class="builder-main">
                <section class="builder-card">
                    <div class="builder-card-header">
                        <h2>{{ t('forms-questions-list_title') }}</h2>
                        <span class="builder-card-count">
                            {{ t('forms-questions-count', [activeQuestions.length]) }}
                        </span>
                    </div>

                    <div class="builder-empty" v-if="!questions.length">
                        <Icon icon-class="ui-message-question-square" size="12" />
                        <p class="builder-empty-title">{{ t('forms-questions-empty_title') }}</p>
                        <p>{{ t('forms-questions-empty_subtitle') }}</p>
                    </div>

                    <draggable
                        v-else
                        class="questions-list"
                        v-model="questions"
                        :options="{ group: 'questions', handle: '.question-drag' }"
                    >
                        <div
                            class="question-row"
                            v-for="(question, index) in questions"
                            :key="question.id"
                            :class="{
                                'is-removed': question.removed,
                                'is-new': question.new,
                            }"
                        >
                            <span class="question-drag">
                                <Icon icon-class="ui-dots-grid" size="4" />
                            </span>
                            <span class="question-type-icon">
                                <Icon :icon-class="getIconByType(question.type)" size="4" />
                            </span>

                            <template v-if="!question.removed">
                                <button
                                    class="question-body"
                                    type="button"
                                    @click="openEditQuestion(question)"
                                >
                                    <!-- i18n-validator-ignore-next-line: runtime question text -->
                                    <span class="question-title">{{ questionPreview(question) }}</span>
                                    <span class="question-meta">
                                        <span class="question-badge">{{ getLabelByType(question.type) }}</span>
                                        <span class="question-badge question-badge--info" v-if="isConditional(question)">
                                            {{ t('forms-questions-conditional') }}
                                        </span>
                                        <span class="question-badge question-badge--new" v-if="question.new">
                                            {{ t('forms-questions-new_question') }}
                                        </span>
                                        <span v-if="question.totalAnswers">
                                            {{ t('forms-questions-answers_count', [question.totalAnswers]) }}
                                        </span>
                                        <!-- i18n-validator-ignore-next-line: runtime option labels -->
                                        <span class="question-options" v-if="question.type === 'selectbox'">
                                            {{ optionsToString(question.options) }}
                                        </span>
                                        <!-- i18n-validator-ignore-next-line: runtime column labels -->
                                        <span class="question-options" v-if="question.type === 'table'">
                                            {{ tableColumnsToString(question.settings) }}
                                        </span>
                                    </span>
                                </button>

                                <div class="question-actions">
                                    <button
                                        v-if="!question.new"
                                        class="question-id"
                                        type="button"
                                        :title="t('forms-questions-copy_id')"
                                        :aria-label="t('forms-questions-copy_id')"
                                        @click="copyQuestionId(question.id)"
                                    >
                                        <Icon
                                            :icon-class="copiedId === question.id ? 'ui-check' : 'ui-copy-03'"
                                            size="2"
                                        />
                                        <!-- i18n-validator-ignore-next-line: runtime question id -->
                                        <span>{{ question.id }}</span>
                                    </button>
                                    <kv-button
                                        variant="text"
                                        size="small"
                                        prepend-icon="ui-edit-05"
                                        :aria-label="t('forms-questions-tooltip_edit')"
                                        @click="openEditQuestion(question)"
                                    />
                                    <kv-button
                                        v-if="index !== 0"
                                        variant="text"
                                        size="small"
                                        prepend-icon="ui-eye"
                                        :aria-label="t('forms-questions-tooltip_condition')"
                                        @click="openQuestionConditionalModal(question, index)"
                                    />
                                    <kv-button
                                        variant="text"
                                        size="small"
                                        prepend-icon="ui-trash-01"
                                        class="question-action-danger"
                                        :aria-label="t('forms-questions-tooltip_delete')"
                                        @click="remove(index)"
                                    />
                                </div>
                            </template>

                            <template v-else>
                                <div class="question-body question-body--removed">
                                    <!-- i18n-validator-ignore-next-line: runtime question text -->
                                    <span class="question-title">{{ questionPreview(question) }}</span>
                                    <span class="question-meta">{{ t('forms-questions-will_remove') }}</span>
                                </div>
                                <div class="question-actions">
                                    <kv-button
                                        variant="text"
                                        size="small"
                                        prepend-icon="ui-refresh-ccw-01"
                                        :aria-label="t('forms-questions-tooltip_undo')"
                                        @click="undoRemove(index)"
                                    />
                                </div>
                            </template>
                        </div>
                    </draggable>
                </section>

                <form-embed
                    :form-id="formsId"
                    :questions="questions"
                    :ask-settings="askSettings"
                />
                </div>

                <aside class="builder-card builder-side">
                    <div class="builder-card-header">
                        <h2>{{ t('forms-settings-title') }}</h2>
                    </div>
                    <p class="builder-card-hint">{{ t('forms-settings-subtitle') }}</p>

                    <div class="ask-field" v-for="field in askFields" :key="field.key">
                        <label :for="`ask-${field.key}`">{{ field.label }}</label>
                        <kv-select
                            :id="`ask-${field.key}`"
                            size="small"
                            variant="outlined"
                            full-width
                            :options="askOptions"
                            v-model="askSettings[field.key]"
                        />
                    </div>

                    <kv-button
                        variant="filled"
                        size="small"
                        prepend-icon="ui-save-01"
                        :disabled="!settingsModified"
                        :loading="savingSettings"
                        @click="saveSettings"
                    >
                        {{ t('forms-settings-btn_save') }}
                    </kv-button>
                </aside>
            </div>

            <div class="save-bar" :class="{ visible: modified }">
                <span class="save-bar-text">
                    {{ t('forms-questions-pending_changes') }}
                    <button type="button" class="save-bar-revert" @click="reset">
                        {{ t('forms-questions-revert_changes') }}
                    </button>
                </span>
                <kv-button
                    variant="filled"
                    prepend-icon="ui-save-01"
                    :loading="is('saving')"
                    @click="save"
                >
                    {{ t('forms-questions-btn_save') }}
                </kv-button>
            </div>

            <question-editor
                ref="questionEditor"
                @save="addQuestion"
                @update="updateQuestion"
            />
            <form-appearance-drawer
                ref="appearanceDrawer"
                :form="form"
                :forms-id="formsId"
                @saved="onAppearanceSaved"
            />
        </template>
    </div>
</template>
<script>
import keviApi from "../keviApi";
import { htmlToText } from "../helpers/html";
import QuestionsConditions from "./questions-conditions/questions-conditions.vue";
import QuestionEditor from "./questions-form/question-editor.vue";
import FormEmbed from "./form-embed/form-embed.vue";
import FormAppearanceDrawer from "./form-appearance-drawer.vue";
import draggable from "vuedraggable";
import Icon from "../components/icon/icon";
import KvButton from "../components/form/kv-button.vue";
import KvSelect from "../components/inputs/kv-select.vue";

const ASK_FIELDS = [
    "askName",
    "askLastName",
    "askEmail",
    "askPhone",
    "askBirthdate",
];

const TYPE_ICONS = {
    emotion: "ui-face-smile",
    comment: "ui-message-text-square-01",
    boolean: "ui-check-square-broken",
    selectbox: "ui-list",
    input: "ui-type-square",
    nps: "ui-bar-chart-07",
    date: "ui-calendar",
    ces: "ui-target-04",
    file: "ui-file-05",
    content: "ui-announcement-02",
    table: "ui-table",
};

export default {
    props: {
        user: Object,
    },
    data: function () {
        return {
            formsId: null,
            form: null,
            state: "loading",
            questions: [],
            questionsDefault: [],
            newCount: 0,
            appMode: window.appMode,
            selectedQuestion: null,
            selectedQuestionIndex: null,
            askSettings: {},
            askSettingsDefault: {},
            savingSettings: false,
            copiedId: null,
            copiedTimeout: null,
        };
    },
    components: {
        draggable,
        QuestionsConditions,
        Icon,
        "question-editor": QuestionEditor,
        "form-embed": FormEmbed,
        "form-appearance-drawer": FormAppearanceDrawer,
        "kv-button": KvButton,
        "kv-select": KvSelect,
    },
    computed: {
        modified: function () {
            return (
                JSON.stringify(this.questions) !==
                JSON.stringify(this.questionsDefault)
            );
        },
        settingsModified() {
            return (
                JSON.stringify(this.askSettings) !==
                JSON.stringify(this.askSettingsDefault)
            );
        },
        hasUnsavedChanges() {
            return this.modified || this.settingsModified;
        },
        activeQuestions() {
            return this.questions.filter((question) => !question.removed);
        },
        answersRoute() {
            return {
                path: "/inbox",
                query: { filter: JSON.stringify({ forms: this.formsId }) },
            };
        },
        askFields() {
            return ASK_FIELDS.map((key) => ({
                key,
                label: this.t(`forms-settings-field_${key}`),
            }));
        },
        askOptions() {
            return [
                { value: "no", label: this.t('forms-settings-ask_no') },
                { value: "optional", label: this.t('forms-settings-ask_optional') },
                { value: "required", label: this.t('forms-settings-ask_required') },
            ];
        },
    },
    mounted: function () {
        document.getElementsByTagName("title")[0].innerText =
            this.t('forms-page_title');
        this.loadQuestions();
        window.addEventListener("beforeunload", this.handleBeforeUnload);
    },
    beforeDestroy: function () {
        clearTimeout(this.copiedTimeout);
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
    },
    beforeRouteLeave(to, from, next) {
        if (!this.hasUnsavedChanges) {
            next();
            return;
        }
        const leave = window.confirm(this.t("forms-questions-leave_unsaved"));
        next(leave);
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        handleBeforeUnload(event) {
            if (!this.hasUnsavedChanges) {
                return;
            }
            event.preventDefault();
            event.returnValue = "";
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        openPublicForm() {
            const slug = this.$store.state.place.slug;
            window.open(
                `${window.baseUrl}#/form/${slug}/${this.formsId}`,
                "_blank",
            );
        },
        openAppearanceSettings() {
            this.$refs.appearanceDrawer.open();
        },
        onAppearanceSaved(savedForm) {
            if (!this.form) {
                return;
            }
            this.form = Object.assign({}, this.form, {
                client_fields_position:
                    savedForm.client_fields_position ||
                    this.form.client_fields_position,
                success_title:
                    savedForm.success_title == null
                        ? ""
                        : savedForm.success_title,
                success_message:
                    savedForm.success_message == null
                        ? ""
                        : savedForm.success_message,
            });
        },
        openQuestionConditionalModal(question, index) {
            this.selectedQuestion = question;
            this.selectedQuestionIndex = index;
            this.$nextTick(() => {
                this.$refs["questionsConditions"].openConditionalModal();
            });
        },
        openNewQuestion() {
            this.$refs.questionEditor.openNew();
        },
        openEditQuestion(question) {
            this.$refs.questionEditor.openEdit(question);
        },
        copyQuestionId(questionId) {
            const value = String(questionId);
            const done = () => {
                this.copiedId = questionId;
                clearTimeout(this.copiedTimeout);
                this.copiedTimeout = setTimeout(() => {
                    this.copiedId = null;
                }, 2000);
                this.$store.dispatch(
                    "notification/success",
                    this.t('forms-questions-id_copied'),
                );
            };

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(value).then(done, done);
                return;
            }
            const helper = document.createElement("textarea");
            helper.value = value;
            document.body.appendChild(helper);
            helper.select();
            document.execCommand("copy");
            document.body.removeChild(helper);
            done();
        },
        generateId: function () {
            this.newCount = this.newCount + 1;
            return "new-" + this.newCount;
        },
        addQuestion: function (question) {
            question = JSON.parse(JSON.stringify(question));
            question.totalAnswers = 0;
            question.removed = 0;
            question.id = this.generateId();
            question.new = true;
            this.questions.push(question);
        },
        updateQuestion: function (question) {
            question = JSON.parse(JSON.stringify(question));
            const index = this.questions.findIndex((q) => q.id == question.id);
            if (index === -1) {
                return;
            }
            question.new = this.questions[index].new;
            this.questions.splice(index, 1, question);
        },
        getIconByType: function (type) {
            return TYPE_ICONS[type] || "ui-file-02";
        },
        getLabelByType: function (type) {
            return this.t(`forms-questions-type_${type}`);
        },
        questionPreview(question) {
            const text = (question.question || "").trim();
            if (question.type !== "content") {
                return text;
            }
            // Content blocks hold rich text; the row only needs its plain text.
            return htmlToText(text).replace(/\s+/g, " ").trim();
        },
        isConditional(question) {
            const condition = question.visibility_condition;
            if (!condition || typeof condition !== "object") {
                return false;
            }
            const conditions = condition.conditions || [];
            const childrens = condition.childrens || [];
            return conditions.length > 0 || childrens.length > 0;
        },
        remove: function (index) {
            if (this.questions[index].new) {
                this.questions.splice(index, 1);
                return;
            }
            this.$set(this.questions[index], "removed", 1);
        },
        undoRemove: function (index) {
            this.$set(this.questions[index], "removed", 0);
        },
        getTemporaryIdMapping: function () {
            const mapping = {};
            this.questions.forEach((question, index) => {
                if (question.new && typeof question.id === 'string' && question.id.startsWith('new-')) {
                    mapping[question.id] = index;
                }
            });
            return mapping;
        },
        save: function () {
            this.setState("saving");
            var options = keviApi.getOptions("form/save-questions", "POST", {
                forms_id: this.formsId,
                questions: this.questions,
                temporary_id_mapping: this.getTemporaryIdMapping(),
            });
            this.$http(options).then(
                function (response) {
                    this.setState("ready");
                    if (response.status === 200) {
                        this.$store.dispatch(
                            "notification/success",
                            this.t('forms-notification_saved'),
                        );
                        this.loadQuestions();
                    }
                },
                (response) => {
                    this.setState("ready");
                    const body = (response && response.body) || {};
                    if (body.message === "invalid-table-settings") {
                        const first = (body.problems || [])[0];
                        this.$store.dispatch(
                            "notification/error",
                            this.t('forms-table-error_server', [
                                htmlToText(body.question || "").trim(),
                                (first && (first.message || first.code)) || "",
                            ]),
                        );
                    }
                },
            );
        },
        // Column titles of a table question, for the list row.
        tableColumnsToString(settings) {
            const columns = (settings && settings.columns) || [];
            return columns.map((col) => col.label).filter(Boolean).join(" · ");
        },
        saveSettings() {
            this.savingSettings = true;
            const options = keviApi.getOptions(
                `form/${this.formsId}/settings`,
                "POST",
                Object.assign({}, this.askSettings),
            );
            this.$http(options).then(
                function (response) {
                    this.savingSettings = false;
                    if (response.status === 200) {
                        this.askSettingsDefault = JSON.parse(
                            JSON.stringify(this.askSettings),
                        );
                        this.$store.dispatch(
                            "notification/success",
                            this.t('forms-settings-notification_saved'),
                        );
                    }
                },
                () => {
                    this.savingSettings = false;
                },
            );
        },
        updateConditions: function (conditions) {
            const actualQuestion = this.questions[this.selectedQuestionIndex];
            actualQuestion.visibility_condition = conditions;
            this.questions.splice(this.selectedQuestionIndex, 1, actualQuestion);
            this.selectedQuestion = null;
            this.selectedQuestionIndex = null;
        },
        reset: function () {
            this.questions = JSON.parse(JSON.stringify(this.questionsDefault));
            this.$store.dispatch(
                "notification/success",
                this.t('forms-notification_reset'),
            );
        },
        loadQuestions: function () {
            if (!this.formsId) {
                return this.$router.push("/forms");
            }
            this.setState("loading");
            var options = keviApi.getOptions(`forms/${this.formsId}`);
            this.$http(options).then(
                function (response) {
                    if (response.status === 200) {
                        this.setState("ready");
                        var questions = response.body.questions || [];
                        for (var q in questions) {
                            questions[q].removed = 0;
                        }
                        this.questionsDefault = questions;
                        this.form = response.body;
                        this.loadSettings(response.body);
                    }
                },
                () => this.$router.push("/forms"),
            );
        },
        loadSettings(form) {
            const settings = {};
            ASK_FIELDS.forEach((key) => {
                settings[key] = form[key] || "no";
            });
            this.askSettings = settings;
            this.askSettingsDefault = JSON.parse(JSON.stringify(settings));
        },
        optionsToString(options) {
            return (options || [])
                .filter((op) => !op.removed)
                .map((op) => op.text)
                .join(", ");
        },
    },
    watch: {
        formsId() {
            this.loadQuestions();
        },
        questionsDefault: function () {
            this.questions = JSON.parse(JSON.stringify(this.questionsDefault));
        },
    },
    beforeMount() {
        this.formsId = this.$route.params.formsId;
    },
};
</script>

<style scoped lang="less">
#form-builder-page {
    padding: 20px 20px 100px;

    @media screen and (max-width: 700px) {
        padding: 12px 12px 120px;
    }
}

.builder-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.builder-heading {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.builder-title {
    display: flex;
    flex-direction: column;
    min-width: 0;

    h1 {
        margin: 0;
        font-size: 20px;
        font-weight: 500;
        color: #111927;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.builder-eyebrow {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #9da4ae;
}

.builder-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.builder-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 16px;
    align-items: start;

    @media screen and (max-width: 991px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

.builder-main {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

.builder-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
}

.builder-card-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    padding: 16px 20px;
    border-bottom: 1px solid #f3f4f6;

    h2 {
        margin: 0;
        font-size: 15px;
        font-weight: 500;
        color: #111927;
    }
}

.builder-card-count {
    font-size: 12px;
    color: #6c737f;
}

.builder-card-hint {
    margin: 0;
    padding: 14px 20px 0;
    font-size: 12px;
    color: #6c737f;
}

.builder-side {
    padding-bottom: 20px;

    .kv-button {
        margin: 4px 20px 0;
    }
}

.ask-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 20px 0;

    label {
        margin: 0;
        font-size: 12px;
        color: #1f2a37;
    }
}

.questions-list {
    display: flex;
    flex-direction: column;
}

.question-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    background: #fff;
    transition: background-color 0.15s ease;

    &:last-child {
        border-bottom: 0;
    }

    &:hover {
        background: #f9fafb;
    }

    &.is-removed {
        background: #fef2f2;
    }

    &.is-new .question-type-icon {
        background: #f1fcf3;
        color: #1d7836;
    }

    &.sortable-ghost {
        opacity: 0.35;
    }
}

.question-drag {
    display: flex;
    color: #d1d5db;
    cursor: grab;
}

.question-type-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #f3f4f6;
    color: #1f2a37;
    flex-shrink: 0;
}

.question-body {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1 1 auto;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
}

.question-title {
    font-size: 14px;
    color: #111927;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.question-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    font-size: 11px;
    color: #6c737f;
}

.question-options {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 320px;
}

.question-badge {
    padding: 1px 7px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #4b5563;

    &--info {
        background: #eff6ff;
        color: #1d4ed8;
    }

    &--new {
        background: #f1fcf3;
        color: #1d7836;
    }
}

.question-actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

.question-id {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-right: 4px;
    padding: 2px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #fff;
    color: #6c737f;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    line-height: 18px;

    &:hover {
        border-color: #2aaa4c;
        color: #1d7836;
    }

    @media screen and (max-width: 767px) {
        display: none;
    }
}

.question-action-danger ::v-deep .ui-icon {
    color: #dc2626;
}

.builder-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 56px 24px;
    color: #6c737f;
    text-align: center;

    p {
        margin: 0;
        font-size: 13px;
    }

    .builder-empty-title {
        font-size: 15px;
        font-weight: 500;
        color: #111927;
    }
}

.save-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 24px;
    background: #fff;
    border-top: 1px solid #e5e7eb;
    box-shadow: 0 -4px 16px rgba(17, 25, 39, 0.06);
    transform: translateY(120%);
    transition: transform 0.25s ease;
    z-index: 9;

    &.visible {
        transform: translateY(0);
    }

    @media screen and (min-width: 701px) {
        left: 230px;
    }
}

.save-bar-text {
    font-size: 13px;
    color: #6c737f;
}

.save-bar-revert {
    margin-left: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #dc2626;
    font-size: 13px;
    text-decoration: underline;
}
</style>
